import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { merge, Observable, of, Subject } from 'rxjs';
import { distinctUntilChanged, filter, map, shareReplay, switchMap, withLatestFrom } from 'rxjs/operators';
import { normalizeNodeId } from '@gt/para/shared/para-node-id.util';
import { ParaApiRootService } from './para-api-root.service';
import { ParaOrgStateService } from './para-org-state.service';

export interface ParaAssessmentSeriesItemDto {
	assessmentId: string;
	version: number;
	label: string;
	window: { id: string; label: string; start: string | null; end: string | null };
	isLocked: boolean;
	isReadOnly: boolean;
	lockReason: string | null;
}

export interface ParaAssessmentSeriesDto {
	nodeId: number;
	instrumentSessionId: number;
	series: ParaAssessmentSeriesItemDto[];
	selectedAssessmentId: string;
	unlock: { isUnlocked: boolean; completedCount: number; requiredCount: number };
}

@Injectable({ providedIn: 'root' })
export class ParaAssessmentSeriesService {
	private readonly httpByNodeId = new Map<number, Observable<ParaAssessmentSeriesDto>>();
	private readonly assessmentSeriesRefreshSubject = new Subject<number>();

	constructor(
		private readonly http: HttpClient,
		private readonly paraApi: ParaApiRootService,
		private readonly paraOrgState: ParaOrgStateService
	) {}

	getSeries$(nodeId: unknown): Observable<ParaAssessmentSeriesDto | null> {
		const nid = normalizeNodeId(nodeId);
		if (nid == null) return of(null);
		let shared = this.httpByNodeId.get(nid);
		if (!shared) {
			shared = this.http
				.get<ParaAssessmentSeriesDto>(this.paraApi.assessmentUrl(`getAssessmentSeries/nodeId/${nid}`))
				.pipe(shareReplay({ bufferSize: 1, refCount: true }));
			this.httpByNodeId.set(nid, shared);
		}
		return shared;
	}

	invalidate(nodeId: unknown): void {
		const nid = normalizeNodeId(nodeId);
		if (nid == null) return;
		this.httpByNodeId.delete(nid);
	}

	refreshAssessmentSeries(triggerNodeId?: unknown): void {
		const trigger = normalizeNodeId(triggerNodeId);
		if (trigger != null) {
			this.invalidate(trigger);
		}
		const scopeId = normalizeNodeId(this.paraOrgState.getSelectedScopeNodeId());
		if (scopeId == null) {
			return;
		}
		this.invalidate(scopeId);
		this.assessmentSeriesRefreshSubject.next(scopeId);
	}

	readonly selectedNodeAssessmentSeries$: Observable<ParaAssessmentSeriesDto | null> = merge(
		this.paraOrgState.selectedParaNodeEntity$.pipe(
			map((n) => normalizeNodeId(n?.id)),
			distinctUntilChanged(),
			switchMap((nid) => (nid == null ? of(null) : this.getSeries$(nid)))
		),
		this.assessmentSeriesRefreshSubject.pipe(
			withLatestFrom(this.paraOrgState.selectedParaNodeEntity$.pipe(map((n) => normalizeNodeId(n?.id)))),
			filter(([rid, sid]) => sid != null && rid === sid),
			switchMap(([rid]) => this.getSeries$(rid))
		)
	).pipe(shareReplay({ bufferSize: 1, refCount: true }));
}
